/**
 * ClientMultizoneHandler.ts
 * 
 * BATON Handler for multizone/grouped playback on the Web Client.
 * 
 * This handler communicates with the Control Bridge's MultizoneHandler
 * to manage synchronized playback across multiple audio renderers with
 * master/slave volume control.
 * 
 * Volume Logic:
 *   - Master volume = actual volume level sent to master renderer
 *   - Slave relative offset = percentage of master (0-100%)
 *   - Slave actual volume = masterVolume * (slaveOffset / 100)
 *   - Slaves can never exceed master (capped at master level)
 * 
 * Commands (to server):
 *   - enable              → Enable multizone mode
 *   - disable             → Disable multizone mode
 *   - getState            → Get current multizone state
 *   - setGroup            → Set which zones are in the group
 *   - addToGroup          → Add a zone to the group
 *   - removeFromGroup     → Remove a zone from the group
 *   - setMaster           → Change which zone is master
 *   - setRelativeVolume   → Set relative volume for a slave zone
 *   - setMasterVolume     → Set master volume (updates all slaves)
 *   - playOnGroup         → Play to all grouped zones
 *   - pauseOnGroup        → Pause all grouped zones
 *   - stopOnGroup         → Stop all grouped zones
 * 
 * Events (from server):
 *   - stateChanged        → Multizone state updated
 * 
 * Usage:
 *   import { getClientMultizoneHandler } from './ClientMultizoneHandler';
 *   
 *   const handler = getClientMultizoneHandler();
 *   handler.connect(webSocket);
 *   
 *   await handler.enable();
 *   await handler.addToGroup('zone_1');
 *   await handler.addToGroup('zone_2');
 *   await handler.setMasterVolume(75);
 */

import { useMaestroStore } from '../store/maestroStore';
import { detectPlatform } from '../utils/platform';

// =============================================================================
// TYPES
// =============================================================================

export interface GroupedZone {
  zoneId: string;
  zoneName: string;
  zoneType: string;
  relativeVolume: number;  // 0-100, percentage of master
  actualVolume: number;    // Calculated actual volume sent to renderer
  isMaster: boolean;
}

export interface MultizoneState {
  enabled: boolean;
  masterZoneId: string | null;
  masterVolume: number;
  groupedZones: GroupedZone[];
}

export interface MultizoneEvent {
  event: string;
  state?: MultizoneState;
  timestamp?: number;
}

export interface MultizoneResponse {
  success: boolean;
  state?: MultizoneState;
  results?: Array<{ zoneId: string; success: boolean; error?: string }>;
  message?: string;
  error?: string;
}

type MultizoneCommandPromise = {
  resolve: (value: MultizoneResponse) => void;
  reject: (reason: Error) => void;
  timeout: NodeJS.Timeout;
};

// =============================================================================
// CLIENT MULTIZONE HANDLER
// =============================================================================

export class ClientMultizoneHandler {
  readonly handlerId = 'multizone';
  readonly location = 'bridge';
  
  private ws: WebSocket | null = null;
  private pendingCommands: Map<string, MultizoneCommandPromise> = new Map();
  private localState: MultizoneState = {
    enabled: false,
    masterZoneId: null,
    masterVolume: 50,
    groupedZones: [],
  };
  
  // Callbacks for UI updates
  private onStateChangedCallbacks: Set<(state: MultizoneState) => void> = new Set();
  
  constructor() {
    console.log('[ClientMultizoneHandler] Initialized');
  }
  
  // ---------------------------------------------------------------------------
  // CONNECTION MANAGEMENT
  // ---------------------------------------------------------------------------
  
  /**
   * Connect to WebSocket (shares connection with other handlers)
   */
  connect(ws: WebSocket): void {
    this.ws = ws;
    console.log('[ClientMultizoneHandler] Connected to WebSocket');
    
    // Fetch initial state
    setTimeout(() => {
      this.getState().then(response => {
        if (response.success && response.state) {
          this.localState = response.state;
          this.notifyStateChanged(response.state);
          this.updateStore(response.state);
        }
      }).catch(err => {
        console.warn('[ClientMultizoneHandler] Failed to fetch initial state:', err);
      });
    }, 500);
  }
  
  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    this.ws = null;
    this.pendingCommands.forEach(promise => {
      clearTimeout(promise.timeout);
      promise.reject(new Error('Disconnected'));
    });
    this.pendingCommands.clear();
    console.log('[ClientMultizoneHandler] Disconnected');
  }
  
  // ---------------------------------------------------------------------------
  // PUBLIC API - Commands
  // ---------------------------------------------------------------------------
  
  /**
   * Enable multizone mode
   */
  async enable(): Promise<MultizoneResponse> {
    const response = await this.sendCommand('multizoneEnable', {});
    if (response.success && response.state) {
      this.localState = response.state;
      this.notifyStateChanged(response.state);
      this.updateStore(response.state);
    }
    return response;
  }
  
  /**
   * Disable multizone mode
   */
  async disable(): Promise<MultizoneResponse> {
    const response = await this.sendCommand('multizoneDisable', {});
    if (response.success && response.state) {
      this.localState = response.state;
      this.notifyStateChanged(response.state);
      this.updateStore(response.state);
    }
    return response;
  }
  
  /**
   * Get current multizone state
   */
  async getState(): Promise<MultizoneResponse> {
    const response = await this.sendCommand('multizoneGetState', {});
    if (response.success && response.state) {
      this.localState = response.state;
    }
    return response;
  }
  
  /**
   * Set the entire group of zones
   * @param zones Array of { zoneId, relativeVolume? }
   */
  async setGroup(zones: Array<{ zoneId: string; relativeVolume?: number }>): Promise<MultizoneResponse> {
    const response = await this.sendCommand('multizoneSetGroup', { zones });
    if (response.success && response.state) {
      this.localState = response.state;
      this.notifyStateChanged(response.state);
      this.updateStore(response.state);
    }
    return response;
  }
  
  /**
   * Add a zone to the group
   * @param zoneId Zone ID to add
   * @param relativeVolume Initial relative volume (0-100, default 100)
   */
  async addToGroup(zoneId: string, relativeVolume: number = 100): Promise<MultizoneResponse> {
    const response = await this.sendCommand('multizoneAddToGroup', { zoneId, relativeVolume });
    if (response.success && response.state) {
      this.localState = response.state;
      this.notifyStateChanged(response.state);
      this.updateStore(response.state);
    }
    return response;
  }
  
  /**
   * Remove a zone from the group
   * @param zoneId Zone ID to remove
   */
  async removeFromGroup(zoneId: string): Promise<MultizoneResponse> {
    const response = await this.sendCommand('multizoneRemoveFromGroup', { zoneId });
    if (response.success && response.state) {
      this.localState = response.state;
      this.notifyStateChanged(response.state);
      this.updateStore(response.state);
    }
    return response;
  }
  
  /**
   * Set which zone is the master
   * @param zoneId Zone ID to make master
   */
  async setMaster(zoneId: string): Promise<MultizoneResponse> {
    const response = await this.sendCommand('multizoneSetMaster', { zoneId });
    if (response.success && response.state) {
      this.localState = response.state;
      this.notifyStateChanged(response.state);
      this.updateStore(response.state);
    }
    return response;
  }
  
  /**
   * Set relative volume for a slave zone
   * @param zoneId Zone ID
   * @param relativeVolume Relative volume (0-100)
   */
  async setRelativeVolume(zoneId: string, relativeVolume: number): Promise<MultizoneResponse> {
    const response = await this.sendCommand('multizoneSetRelativeVolume', { zoneId, relativeVolume });
    if (response.success && response.state) {
      this.localState = response.state;
      this.notifyStateChanged(response.state);
      this.updateStore(response.state);
    }
    return response;
  }
  
  /**
   * Set master volume (updates all slaves proportionally)
   * @param volume Master volume (0-100)
   */
  async setMasterVolume(volume: number): Promise<MultizoneResponse> {
    const response = await this.sendCommand('multizoneSetMasterVolume', { volume });
    if (response.success && response.state) {
      this.localState = response.state;
      this.notifyStateChanged(response.state);
      this.updateStore(response.state);
    }
    return response;
  }
  
  /**
   * Play on all grouped zones
   * @param trackInfo Track metadata
   */
  async playOnGroup(trackInfo: {
    streamUrl: string;
    title?: string;
    artist?: string;
    album?: string;
    artworkUrl?: string;
    duration?: number;
    format?: string;
  }): Promise<MultizoneResponse> {
    return this.sendCommand('multizonePlayOnGroup', trackInfo);
  }
  
  /**
   * Resume all grouped zones (Play action, NOT play_uri — preserves position)
   */
  async resumeOnGroup(): Promise<MultizoneResponse> {
    return this.sendCommand('multizoneResumeOnGroup', {});
  }

  /**
   * Pause all grouped zones
   */
  async pauseOnGroup(): Promise<MultizoneResponse> {
    return this.sendCommand('multizonePauseOnGroup', {});
  }
  
  /**
   * Stop all grouped zones
   */
  async stopOnGroup(): Promise<MultizoneResponse> {
    return this.sendCommand('multizoneStopOnGroup', {});
  }
  
  /**
   * Seek on all grouped zones
   * @param position Position in milliseconds
   */
  async seekOnGroup(position: number): Promise<MultizoneResponse> {
    return this.sendCommand('multizoneSeekOnGroup', { position });
  }
  
  /**
   * Get cached state (synchronous)
   */
  getCachedState(): MultizoneState {
    return { ...this.localState };
  }
  
  /**
   * Check if multizone is enabled
   */
  isEnabled(): boolean {
    return this.localState.enabled;
  }
  
  /**
   * Check if a zone is in the group
   */
  isInGroup(zoneId: string): boolean {
    return this.localState.groupedZones.some(z => z.zoneId === zoneId);
  }
  
  /**
   * Get the master zone ID
   */
  getMasterZoneId(): string | null {
    return this.localState.masterZoneId;
  }
  
  /**
   * Get master volume
   */
  getMasterVolume(): number {
    return this.localState.masterVolume;
  }
  
  // ---------------------------------------------------------------------------
  // EVENT CALLBACKS
  // ---------------------------------------------------------------------------
  
  /**
   * Subscribe to state changed events
   */
  onStateChanged(callback: (state: MultizoneState) => void): () => void {
    this.onStateChangedCallbacks.add(callback);
    return () => this.onStateChangedCallbacks.delete(callback);
  }
  
  // ---------------------------------------------------------------------------
  // EVENT HANDLING (from server)
  // ---------------------------------------------------------------------------
  
  /**
   * Handle incoming BATON events
   */
  handleEvent(event: MultizoneEvent): void {
    const eventType = event.event;
    console.log(`[ClientMultizoneHandler] 📥 Event received: ${eventType}`, event);
    
    switch (eventType) {
      case 'stateChanged':
        if (event.state) {
          console.log(`[ClientMultizoneHandler] 📊 State update: enabled=${event.state.enabled}, zones=${event.state.groupedZones?.length || 0}, master=${event.state.masterZoneId}`);
          this.localState = event.state;
          this.notifyStateChanged(event.state);
          this.updateStore(event.state);
          console.log('[ClientMultizoneHandler] ✅ Store updated with new state');
        }
        break;
        
      default:
        console.warn(`[ClientMultizoneHandler] Unknown event: ${eventType}`);
    }
  }
  
  /**
   * Handle BATON response
   */
  handleResponse(message: { id: string; payload: MultizoneResponse }): void {
    const id = message.id;
    const pending = this.pendingCommands.get(id);
    
    if (pending) {
      clearTimeout(pending.timeout);
      this.pendingCommands.delete(id);
      pending.resolve(message.payload);
    } else {
      console.warn('[ClientMultizoneHandler] No pending command for id:', id);
    }
  }
  
  // ---------------------------------------------------------------------------
  // INTERNAL - Command Sending
  // ---------------------------------------------------------------------------
  
  private async sendCommand(cmd: string, payload: Record<string, unknown>): Promise<MultizoneResponse> {
    return new Promise((resolve, reject) => {
      const messageId = `multizone_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      
      // Build BATON message envelope
      const message = {
        v: 1,
        id: messageId,
        ts: Date.now(),
        type: 'command',
        handler: 'multizone',
        location: 'local',
        payload: { cmd, ...payload },
      };
      
      // Set up timeout
      const timeout = setTimeout(() => {
        this.pendingCommands.delete(messageId);
        console.warn(`[ClientMultizoneHandler] Command ${cmd} timed out`);
        reject(new Error(`Command ${cmd} timed out`));
      }, 10000);
      
      // Store pending promise
      this.pendingCommands.set(messageId, { resolve, reject, timeout });
      
      // iOS native app: send to native bridge for local multizone handling
      const iosBridge = detectPlatform().isIOSNativeApp
        ? ((window as any).webkit?.messageHandlers?.baton || (window as any).__MAESTRO_BRIDGE__)
        : null;
      if (iosBridge) {
        const json = JSON.stringify(message);
        console.log(`[ClientMultizoneHandler] 📱 Sending to iOS native: ${cmd}`);
        iosBridge.postMessage(json);
        return;
      }
      
      // Fallback: Send via WebSocket to server (development mode / non-iOS)
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this.pendingCommands.delete(messageId);
        clearTimeout(timeout);
        reject(new Error('Not connected and no iOS bridge available'));
        return;
      }
      
      this.ws.send(JSON.stringify(message));
      console.log(`[ClientMultizoneHandler] 🌐 Sent via WebSocket: ${cmd} (id: ${messageId})`);
    });
  }
  
  // ---------------------------------------------------------------------------
  // INTERNAL - Notification & Store Updates
  // ---------------------------------------------------------------------------
  
  private notifyStateChanged(state: MultizoneState): void {
    this.onStateChangedCallbacks.forEach(cb => {
      try {
        cb(state);
      } catch (e) {
        console.error('[ClientMultizoneHandler] Callback error:', e);
      }
    });
  }
  
  private updateStore(state: MultizoneState): void {
    const { setMultizoneState } = useMaestroStore.getState();
    if (setMultizoneState) {
      setMultizoneState(state);
    }
  }
  
  // ---------------------------------------------------------------------------
  // BATON HANDLER INTERFACE
  // ---------------------------------------------------------------------------
  
  canHandle(messageType: string): boolean {
    return messageType.startsWith('multizone:');
  }
  
  getInfo() {
    return {
      handlerId: this.handlerId,
      location: this.location,
      commands: [
        'multizone:enable',
        'multizone:disable',
        'multizone:getState',
        'multizone:setGroup',
        'multizone:addToGroup',
        'multizone:removeFromGroup',
        'multizone:setMaster',
        'multizone:setRelativeVolume',
        'multizone:setMasterVolume',
        'multizone:playOnGroup',
        'multizone:pauseOnGroup',
        'multizone:stopOnGroup',
      ],
      events: [
        'multizone:stateChanged',
      ],
    };
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

let clientMultizoneHandlerInstance: ClientMultizoneHandler | null = null;

export function getClientMultizoneHandler(): ClientMultizoneHandler {
  if (!clientMultizoneHandlerInstance) {
    clientMultizoneHandlerInstance = new ClientMultizoneHandler();
  }
  return clientMultizoneHandlerInstance;
}

